import { writable } from 'svelte/store';
import { api, type UploadedDocument, type UploadBatchStatus } from '../api';

interface UploadSession {
  uploadBatchId: string | null;
  documents: UploadedDocument[];
  selectedDocumentId: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  polling: boolean;
  totalDocuments: number;
  processedDocuments: number;
  failedDocuments: number;
}

const INITIAL_STATE: UploadSession = {
  uploadBatchId: null,
  documents: [],
  selectedDocumentId: null,
  status: 'pending',
  polling: false,
  totalDocuments: 0,
  processedDocuments: 0,
  failedDocuments: 0,
};

const POLL_FACTOR = 1.5; // Exponential backoff factor
const MAX_POLL_DELAY = 5000; // Max 5 seconds
const INITIAL_POLL_DELAY = 1000; // Start with 1 second

function createUploadSessionStore() {
  const { subscribe, set, update } = writable<UploadSession>(INITIAL_STATE);

  let pollingInterval: number | null = null;
  let pollDelay = INITIAL_POLL_DELAY;
  let saving = false;

  function mergeDocuments(
    existing: UploadedDocument[],
    incoming: UploadBatchStatus['documents'],
  ): UploadedDocument[] {
    const existingMap = new Map(existing.map((d) => [d.id, d]));
    return incoming.map((doc) => {
      const prev = existingMap.get(doc.id);
      return {
        id: doc.id,
        fileName: doc.fileName,
        fileSize: prev?.fileSize ?? 0,
        mimeType: prev?.mimeType ?? '',
        status: doc.status,
        uploadedAt: prev?.uploadedAt ?? new Date().toISOString(),
        classification: doc.classification ?? null,
        extractedData: doc.extractedData ?? null,
        assignedTo: doc.assignedTo ?? prev?.assignedTo ?? [],
        userTags: doc.userTags ?? prev?.userTags ?? [],
        householdId: doc.householdId ?? prev?.householdId ?? null,
      };
    });
  }

  async function pollUploadStatus(uploadBatchId: string) {
    try {
      const batchStatus: UploadBatchStatus = await api.upload.getUploadBatchStatus(uploadBatchId);

      // Skip document updates while saving to prevent overwriting local changes
      if (saving) {
        // Still update batch-level status (progress counters)
        update((session) => ({
          ...session,
          status: batchStatus.status,
          totalDocuments: batchStatus.totalDocuments,
          processedDocuments: batchStatus.processedDocuments,
          failedDocuments: batchStatus.failedDocuments,
        }));
      } else {
        update((session) => ({
          ...session,
          status: batchStatus.status,
          totalDocuments: batchStatus.totalDocuments,
          processedDocuments: batchStatus.processedDocuments,
          failedDocuments: batchStatus.failedDocuments,
          documents: mergeDocuments(session.documents, batchStatus.documents),
        }));
      }

      // Check if upload is complete
      if (batchStatus.status === 'completed' || batchStatus.status === 'failed') {
        stopPolling();
      } else {
        // Continue polling with exponential backoff
        scheduleNextPoll(uploadBatchId);
      }
    } catch (err) {
      console.error('Failed to poll upload status:', err);
      stopPolling();
      update((session) => ({
        ...session,
        status: 'failed',
        polling: false,
      }));
    }
  }

  function scheduleNextPoll(uploadBatchId: string) {
    pollingInterval = window.setTimeout(() => {
      pollUploadStatus(uploadBatchId);
      // Increase delay for next poll (exponential backoff)
      pollDelay = Math.min(pollDelay * POLL_FACTOR, MAX_POLL_DELAY);
    }, pollDelay);
  }

  function stopPolling() {
    if (pollingInterval !== null) {
      clearTimeout(pollingInterval);
      pollingInterval = null;
    }
    update((session) => ({
      ...session,
      polling: false,
    }));
  }

  return {
    subscribe,

    /**
     * Start a new upload session
     */
    startSession(uploadBatchId: string, documents: UploadedDocument[]): void {
      pollDelay = INITIAL_POLL_DELAY; // Reset poll delay
      set({
        uploadBatchId,
        documents: documents.map((doc) => ({
          ...doc,
          classification: doc.classification ?? null,
          extractedData: doc.extractedData ?? null,
          assignedTo: doc.assignedTo ?? [],
          userTags: doc.userTags ?? [],
          householdId: doc.householdId ?? null,
        })),
        selectedDocumentId: documents.length > 0 ? documents[0].id : null,
        status: 'processing',
        polling: false,
        totalDocuments: documents.length,
        processedDocuments: 0,
        failedDocuments: 0,
      });
    },

    /**
     * Select a document for detail view
     */
    selectDocument(documentId: string | null): void {
      update((session) => ({
        ...session,
        selectedDocumentId: documentId,
      }));
    },

    /**
     * Start polling for status updates
     */
    startPolling(): void {
      let currentBatchId: string | null = null;

      // Get current uploadBatchId from store
      subscribe((session) => {
        currentBatchId = session.uploadBatchId;
      })();

      if (!currentBatchId) {
        console.warn('Cannot start polling: no uploadBatchId set');
        return;
      }

      update((session) => ({
        ...session,
        polling: true,
      }));

      // Start polling immediately
      pollUploadStatus(currentBatchId);
    },

    /**
     * Stop polling
     */
    stopPolling(): void {
      stopPolling();
    },

    /**
     * Update status manually (for external updates)
     */
    updateStatus(status: UploadBatchStatus): void {
      update((session) => ({
        ...session,
        status: status.status,
        totalDocuments: status.totalDocuments,
        processedDocuments: status.processedDocuments,
        failedDocuments: status.failedDocuments,
        documents: status.documents.map((doc) => ({
          id: doc.id,
          fileName: doc.fileName,
          fileSize: 0,
          mimeType: '',
          status: doc.status,
          uploadedAt: new Date().toISOString(),
          classification: doc.classification ?? null,
          extractedData: doc.extractedData ?? null,
          assignedTo: doc.assignedTo ?? [],
          userTags: doc.userTags ?? [],
          householdId: doc.householdId ?? null,
        })),
      }));
    },

    /**
     * Load an existing session from the API (for page reload / deep link)
     */
    async loadSession(batchId: string): Promise<void> {
      const batchStatus: UploadBatchStatus = await api.upload.getUploadBatchStatus(batchId);
      set({
        uploadBatchId: batchId,
        documents: batchStatus.documents.map((doc) => ({
          id: doc.id,
          fileName: doc.fileName,
          fileSize: 0,
          mimeType: '',
          status: doc.status,
          uploadedAt: new Date().toISOString(),
          classification: doc.classification ?? null,
          extractedData: doc.extractedData ?? null,
          assignedTo: doc.assignedTo ?? [],
          userTags: doc.userTags ?? [],
          householdId: doc.householdId ?? null,
        })),
        selectedDocumentId: batchStatus.documents.length > 0 ? batchStatus.documents[0].id : null,
        status: batchStatus.status,
        polling: false,
        totalDocuments: batchStatus.totalDocuments,
        processedDocuments: batchStatus.processedDocuments,
        failedDocuments: batchStatus.failedDocuments,
      });
    },

    /**
     * Signal that a save is in progress (prevents polling from overwriting local changes)
     */
    setSaving(value: boolean): void {
      saving = value;
    },

    /**
     * Update a single document in the session (e.g. after saving)
     */
    updateDocument(documentId: string, changes: Partial<UploadedDocument>): void {
      update((session) => ({
        ...session,
        documents: session.documents.map((doc) =>
          doc.id === documentId ? { ...doc, ...changes } : doc,
        ),
      }));
    },

    /**
     * Reset the upload session
     */
    reset(): void {
      stopPolling();
      pollDelay = INITIAL_POLL_DELAY;
      set(INITIAL_STATE);
    },
  };
}

export const uploadSessionStore = createUploadSessionStore();
